import React, { useCallback, useRef } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { tokens as c } from '../../theme';

/**
 * The search box of the approved list pages: a magnifier, the hint as the
 * placeholder AND the accessible name (no separate label), a clear button
 * once something is typed, 44 px high. The X puts the cursor back in the box
 * (call centre: clear, then type the next name or phone at once).
 *
 *   <SearchField value={q} onChange={setQ} placeholder={t('drivers_search_hint', { defaultValue: 'Search name, phone or email' })} />
 *
 * onChange receives the text, not the event.
 */
export default function SearchField({ value, onChange, placeholder, width, autoFocus = false, sx, inputRef }) {
  const { t } = useTranslation();
  const hint = placeholder || t('search', { defaultValue: 'Search' });
  const text = value === null || value === undefined ? '' : String(value);
  const ownRef = useRef(null);
  const setInput = useCallback((el) => {
    ownRef.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) inputRef.current = el;
  }, [inputRef]);
  const clear = () => {
    onChange('');
    if (ownRef.current) ownRef.current.focus();
  };
  return (
    <TextField
      type="search"
      value={text}
      onChange={(e) => onChange(e.target.value)}
      placeholder={hint}
      autoFocus={autoFocus}
      inputRef={setInput}
      inputProps={{ 'aria-label': hint, autoComplete: 'off', spellCheck: false }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon aria-hidden="true" sx={{ color: c.INK_2 }} />
          </InputAdornment>
        ),
        endAdornment: text ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              // A mouse press never takes the cursor out of the box.
              onMouseDown={(e) => e.preventDefault()}
              onClick={clear}
              aria-label={t('clear_search', { defaultValue: 'Clear search' })}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        width: width || { xs: '100%', md: 280 },
        '& .MuiOutlinedInput-root': { minHeight: 44 },
        // The browser's own clear cross would sit next to ours.
        '& input[type=search]::-webkit-search-cancel-button': { display: 'none' },
        ...(sx || {}),
      }}
    />
  );
}
